import React, { useState, useEffect, useRef } from "react";

export default function MultiSelectAutocomplete({
  options = [],
  value = [],
  onChange,
  placeholder = "Type to search...",
  mode = "multiselect", // "multiselect" | "showAll"
  keepOpenOnSelect = true,
  nonRemovableItems = [],
}) {
  const [inputValue, setInputValue] = useState("");
  const [selectedItems, setSelectedItems] = useState(value || []);
  const [suggestions, setSuggestions] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const wrapperRef = useRef();

  // Sync with parent
  useEffect(() => {
    setSelectedItems(value || []);
  }, [value]);

  // Filter / show suggestions
  useEffect(() => {
    const val = inputValue.toLowerCase();
    const filtered = options.filter((o) => {
      const valLower = inputValue.toLowerCase();
      // always filter by inputValue
      const match = !valLower || o.toLowerCase().includes(valLower); 
      const notSelected = !selectedItems.includes(o);
      return match && notSelected;
    });

    setSuggestions(filtered);
  }, [inputValue, options, selectedItems, mode]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        if (mode !== "showAll") {
          setIsFocused(false);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mode]);

  const addItem = (item) => {
    const newItems = [...selectedItems, item];
    setSelectedItems(newItems);
    onChange?.(newItems);

    if (mode === "multiselect") {
      setInputValue("");
    }

    if (!keepOpenOnSelect && mode === "multiselect") {
      setIsFocused(false);
    }
  };

  const removeItem = (item) => {
    const newItems = selectedItems.filter((i) => i !== item);
    setSelectedItems(newItems);
    onChange?.(newItems);
  };

  const renderChips = () => (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        marginBottom: mode === "showAll" ? 8 : 0,
      }}
    >
      {selectedItems.map((item) => {
        const isLocked = nonRemovableItems.includes(item);

        return (
          <span key={item} className="chipStyle">
            {item}
            {!isLocked && (
              <button
                className="label-btn chip"
                onClick={() => removeItem(item)}
              >
                ×
              </button>
            )}
          </span>
        );
      })}

    </div>
  );

  return (
    <div ref={wrapperRef} style={{ width: 300, position: "relative" }}>
      {/* Chips OUTSIDE input (showAll mode) */}
      {mode === "showAll" && renderChips()}

      {/*JSON.stringify(options, null, 2)*/}

      {/* Input wrapper */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 5,
          border: "1px solid #ccc",
          padding: 6,
          borderRadius: 6,
        }}
        onClick={() => mode !== "showAll" && setIsFocused(true)}
      >
        {/* Chips INSIDE input (multiselect mode) */}
        {mode === "multiselect" &&
          selectedItems.map((item) => {
        const isLocked = nonRemovableItems.includes(item);
        
          return (
            <span key={item} className="chipStyle"
            style={{
                  background: "#E3F2FD",
                  color: "#0D47A1",
                  padding: "3px 8px",
                  borderRadius: 16,
                  display: "flex",
                  alignItems: "center",
                }}
            >
              {item}
              {!isLocked && (
                <button
                  className="label-btn chip"
                  onClick={() => removeItem(item)}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}

        <input
          type="text"
          value={inputValue}
          placeholder={placeholder}
          onFocus={() => mode !== "showAll" && setIsFocused(true)}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (mode !== "showAll") setIsFocused(true);
          }}
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            minWidth: 100,
          }}
        />
      </div>

      {/* Suggestions */}
      {(mode === "showAll" || isFocused) && suggestions.length > 0 && (
        <ul
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            maxHeight: 180,
            overflowY: "auto",
            background: "white",
            listStyle: "none",
            margin: 0,
            padding: 0,
            zIndex: 1000,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {suggestions.map((s) => (
            <li
              key={s}
              onClick={() => addItem(s)}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                borderBottom: "1px solid #eee",
              }}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
